import { CLASSNAME_OVERRIDE_MAP } from '../constants';
import { ChatBlkTelemetryService } from '../services';
import { ChatBlkPromptSource, telemetryFunctionMap } from '../types';

/**
 * Custom hook that provides overrides for markdown rendering.
 * @param id
 * @param chatBlkEventId
 * @param response
 * @param sources
 * @returns The markdown overrides utils
 */
const useMarkdownOverride = (
    id?: string,
    sources?: ChatBlkPromptSource[]
): {
    overriddenElements: string[];
    preprocessMarkdown: (markdown: string) => string;
    isSource: (children: JSX.Element, href: string) => boolean;
    createLinkOverride: (
        href: string,
        children: JSX.Element,
        linkNode: Element
    ) => { href: string; label: string; onClick: () => void };
    shouldRenderPDFIcon: (href: string) => boolean;
} => {
    const overriddenElements = Object.keys(CLASSNAME_OVERRIDE_MAP);
    const preprocessMarkdown = (markdown: string) => {
        return markdown.replace(/\[(\d+)\]/g, (match, number) => {
            const source = sources[Number(number) - 1];
            return source ? `[${number}](${source.uri})` : match;
        });
    };

    const shouldRenderPDFIcon = (href: string): boolean => {
        return /\.pdf$/i.test(href);
    };

    const getLabel = (linkNode: Element) => {
        const linkLabel = '';

        const rebuildLabel = (node: Element, label: string) => {
            if (!node.children) return label;

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (node as any).children.forEach((child) => {
                if (child.type === 'text') {
                    label += child.value;
                }
                if (child.type === 'element') {
                    label = rebuildLabel(child, label);
                }
            });
            return label;
        };
        return rebuildLabel(linkNode, linkLabel);
    };

    const isSource = (children: JSX.Element, href: string) => {
        const label = children?.[0] ?? '';
        // is source if the href and label pair is found in the sources array
        return !!sources?.find(
            (source) => source.uri === href && source.id === label
        );
    };

    const createLinkOverride = (
        href: string,
        children: JSX.Element,
        linkNode: Element
    ) => {
        const label = children?.[0] ?? '';
        const trackTelemetry = () => {
            ChatBlkTelemetryService.track(
                telemetryFunctionMap.CHATBLK_MFE_FUNCTION_QUERY_RESPONSE_LINK_CLICKED,
                { href, label },
                id
            );
        };
        return {
            href,
            label: getLabel(linkNode),
            onClick: trackTelemetry,
        };
    };

    return {
        overriddenElements,
        createLinkOverride,
        isSource,
        preprocessMarkdown,
        shouldRenderPDFIcon,
    };
};

export default useMarkdownOverride;
